/**
 * «Переслать в салон»: сообщение клиента из «Чатов» — в группу Telegram «Общее <город>».
 *
 * Город подставлен по чату, сотрудник может выбрать другой. В группу уходят текст и фото
 * сообщения, номер клиента, ссылки на его карточку и сделку в CRM и отметка стилиста, который
 * вёл последнюю примерку. Ответ салона реплаем появится в полосе над полем ответа, как у подбора.
 * Пачка (режим выделения ленты) — одним запросом: фото альбомом, тексты перепиской по порядку.
 * Спека `docs/specs/2026-10-07-chat-forward-design.md`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Forward, X } from "lucide-react";
import { useEffect, useState } from "react";

import { fmtPhone } from "@/components/chat/Contacts";
import { plural } from "@/lib/plural";
import { type ForwardMessage, loadForward, sendForward } from "@/lib/podbor";

export function ForwardDialog({
  chatId,
  messageIds,
  onClose,
  onSent,
}: {
  chatId: number;
  messageIds: number[];
  onClose: () => void;
  /** Ушло — например, выйти из режима выделения. */
  onSent?: () => void;
}) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["chat-forward", chatId, ...messageIds], queryFn: () => loadForward(chatId, messageIds) });
  const [city, setCity] = useState<number | null>(null);
  const [text, setText] = useState("");
  const f = q.data;
  const chosen = city ?? f?.city ?? null;
  const group = f?.cities.find((c) => c.id === chosen)?.group;
  const send = useMutation({
    mutationFn: () => sendForward(chatId, { messages: messageIds, city: chosen!, text }),
    onSuccess: () => {
      onSent?.();
      void qc.invalidateQueries({ queryKey: ["chat-podbor", chatId] });
      void qc.invalidateQueries({ queryKey: ["chat-actions", chatId] });
      void qc.invalidateQueries({ queryKey: ["client-chat", chatId] });
      onClose();
    },
  });
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3" onClick={onClose}>
      <div
        className="flex max-h-full w-full max-w-[520px] flex-col overflow-hidden rounded-[16px] bg-bg text-[14px] text-fg"
        onClick={(e) => e.stopPropagation()}
        data-annot="переслать в салон"
      >
        <div className="flex flex-none items-center gap-2 border-b border-border px-4 py-2.5">
          <Forward size={18} className="flex-none" />
          <div className="min-w-0 flex-1 text-[15px] font-medium">
            Переслать в чат салона
            {messageIds.length > 1 && ` · ${messageIds.length} ${plural(messageIds.length, "сообщение", "сообщения", "сообщений")}`}
          </div>
          <button type="button" onClick={onClose} title="Закрыть" className="grid size-8 place-items-center rounded-full hover:bg-bg-subtle">
            <X size={18} />
          </button>
        </div>
        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-4 py-3">
          {q.isPending && <div className="text-fg-muted">Загрузка…</div>}
          {q.isError && <div className="text-[#e53935]">{q.error.message}</div>}
          {f && (
            <>
              {f.messages.length > 1 ? (
                <ul className="flex max-h-[240px] flex-col gap-2 overflow-y-auto rounded-[10px] border border-border bg-bg-subtle p-2.5" data-annot="что пересылаем">
                  {f.messages.map((m) => (
                    <li key={m.id}>
                      <Preview chatId={chatId} m={m} small />
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="rounded-[10px] border border-border bg-bg-subtle p-2.5" data-annot="что пересылаем">
                  <Preview chatId={chatId} m={f.message} />
                </div>
              )}
              <label className="flex flex-col gap-1" data-annot="город пересылки">
                <span className="text-[12px] text-fg-muted">Куда — город{f.city === null && " (у чата город не определён — выберите)"}</span>
                <select
                  value={chosen ?? ""}
                  onChange={(e) => setCity(e.target.value ? Number(e.target.value) : null)}
                  className="rounded-[8px] border border-border bg-bg px-2.5 py-1.5"
                >
                  {chosen === null && <option value="">— выберите город —</option>}
                  {f.cities.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} — «{c.group}»
                    </option>
                  ))}
                </select>
              </label>
              <div className="flex flex-col gap-0.5 text-[13px]" data-annot="что добавим к сообщению">
                <div>
                  <span className="text-fg-muted">Клиент: </span>
                  {f.phone ? fmtPhone(String(f.phone)) : "номер неизвестен"}
                  {f.customer ? " · ссылка на карточку клиента" : ""}
                  {f.deal ? ` · сделка ${f.deal}` : ""}
                </div>
                <div>
                  <span className="text-fg-muted">Отметим: </span>
                  {f.stylist
                    ? `${f.stylist.name} — вела примерку ${f.stylist.date.slice(8, 10)}.${f.stylist.date.slice(5, 7)}${f.stylist.telegram ? "" : " (Telegram неизвестен — только имя)"}`
                    : "некого — примерок по сделке чата нет"}
                </div>
              </div>
              <label className="flex flex-col gap-1">
                <span className="text-[12px] text-fg-muted">Комментарий для салона (необязательно)</span>
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  rows={3}
                  maxLength={1000}
                  placeholder="Что нужно от салона"
                  className="resize-y rounded-[8px] border border-border bg-bg px-2.5 py-1.5"
                />
              </label>
            </>
          )}
          {send.isError && <div className="text-[#e53935]">{send.error.message}</div>}
        </div>
        <div className="flex flex-none items-center justify-end gap-2 border-t border-border px-4 py-2.5">
          <button type="button" onClick={onClose} className="rounded-full px-3.5 py-1.5 font-medium text-fg-muted hover:bg-bg-subtle">
            Отмена
          </button>
          <button
            type="button"
            disabled={!f || chosen === null || send.isPending}
            onClick={() => send.mutate()}
            title="Сообщение уйдёт сотрудникам салона в группу Telegram"
            className="rounded-full bg-[#3390ec] px-4 py-1.5 font-medium text-white disabled:opacity-50"
            data-annot="отправить в салон"
          >
            {send.isPending ? "Отправляю…" : group ? `Отправить в «${group}»` : "Отправить"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Сообщение в окне: фото миниатюрой, кто и когда писал, текст. */
function Preview({ chatId, m, small }: { chatId: number; m: ForwardMessage; small?: boolean }) {
  const who = m.out ? (small ? "Мы" : "Наше сообщение клиенту") : small ? "Клиент" : "Сообщение клиента";
  return (
    <div className="flex gap-3">
      {m.photo && (
        <img
          src={`/api/chats/${chatId}/file/${m.id}?thumb=true`}
          alt="Фото из сообщения"
          className={`flex-none rounded-[8px] bg-black/10 object-cover ${small ? "h-[56px] w-[44px]" : "h-[96px] w-[76px]"}`}
        />
      )}
      <div className={`min-w-0 whitespace-pre-wrap break-words ${small ? "" : "max-h-[140px] overflow-y-auto"}`}>
        <div className="text-[12px] text-fg-muted">
          {who}
          {small && ` · ${m.date.slice(8, 10)}.${m.date.slice(5, 7)} ${m.date.slice(11, 16)}`}
        </div>
        {m.text || (m.photo ? "‹фото без текста›" : m.file ? "‹вложение — в группу уйдёт пометкой›" : "‹без текста›")}
      </div>
    </div>
  );
}
